import React, { useContext, useEffect, useRef, useState } from 'react';
import Button from '@/components/atoms/button';
import useTranslation from '@/providers/I18n/hooks/useTranslation';
import { CheckIcon, XMarkIcon as CloseIcon } from '@heroicons/react/24/outline';
import Image from '@/components/atoms/Image';
import Typography from '@/components/atoms/typography';
import IconButton from '@/components/atoms/icon-button';
import FileIcon from 'public/images/file.svg';
import useStores from '@/lib/hooks/useStores';
import useProductSearch from '@/lib/hooks/useProductSearch';
import { mapCsvProduct } from '@/utils/mappers/map-csv-product';
import toast from 'react-hot-toast';
import { Product } from '@shared/types/product';
import LoadingIcon from '@/components/atoms/loading-icon';
import useCSVExport from '../hooks/useCSVExport';
import { QuickOrderDesktopContext } from '../context';
import { ProductPackagingTypeList } from '@shared/types/product/Product';
import { useRootCategories } from '@/lib/hooks/useCategories';
import { useStoreAndBusinessUnits } from '@/providers/store-and-business-units';
import { format } from 'date-fns';
import Accordion from '@/components/molecules/accordion';

type UploadPanelProps = {
  onClose: () => void;
};

const UploadPanel = ({ onClose }: UploadPanelProps) => {
  const inputFile = useRef<HTMLInputElement>(null);
  const { translate } = useTranslation();
  const { exportToCSV } = useCSVExport();
  const { selectedStore, channel, priceChannel } = useStores();
  const { selectedBusinessUnit } = useStoreAndBusinessUnits();
  const [exportProgress, setExportProgress] = useState<number>(0);
  const [isExportLoading, setIsExportLoading] = useState(false);
  const [fetchedProducts, setFetchedProducts] = useState<Product[]>([]);
  const [commercialOperationKey, setCommercialOperationKey] = useState<string | undefined>(undefined);
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);

  const { categories: commercialOperations } = useRootCategories({
    isCommercialOperation: true,
    inDate: format(new Date(), 'yyyy-MM-dd'),
    businessUnitKey: selectedBusinessUnit?.key,
    storeKey: selectedStore?.key,
  });

  const {
    files,
    loading,
    addToCartLoading,
    addToCartError,
    loadingProgress,
    fileError,
    handleChange,
    handleClearFiles,
    handleClearReadFiles,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleRemoveClick,
    handleUploadClick,
    handleClearAddToCartError,
  } = useContext(QuickOrderDesktopContext);

  const handleClearClick = () => {
    handleClearFiles();
    handleClearReadFiles();
  };

  const fileNameClassNames = (file: File) => {
    return `rounded-md border ${fileError[file.name] && 'border-red-500'} ${!loading[file.name] && 'border-green-500'}`;
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;
    handleChange(e);
    if (inputFile.current) inputFile.current.value = '';
  };

  const onRemoveClick = (file: File) => {
    handleRemoveClick(file);
    if (inputFile.current) inputFile.current.value = '';
  };

  const loadingBarClassName = (file: File) => `w-[${loadingProgress[file.name]}%] bg-primary h-1`;

  const { loadManually } = useProductSearch();

  const isUploadEnabled = files.length !== 0 && Object.keys(fileError).length === 0;

  const handleDownloadCSV = async (params: {
    productTypeKey?: string;
    categoryId?: string;
    categoryKey?: string;
    isCommercialOperation: boolean;
  }) => {
    setFetchedProducts([]);
    setIsExportLoading(false);
    setExportProgress(0);
    setCommercialOperationKey(params.categoryKey);

    try {
      const products = await loadManually({
        skus: undefined,
        limit: undefined,
        storeKey: selectedStore?.key,
        customerCategoryChannelKey: channel?.key,
        distributionChannelId: priceChannel?.channelId,
        isInitialSearch: true,
        getAllProducts: true,
        productTypeKey: params.productTypeKey,
        isCommercialOperation: params.isCommercialOperation,
        category: params.categoryId,
      });

      if (!products || products.length === 0) {
        const emptyMessageToTranslate =
          params.productTypeKey === ProductPackagingTypeList.BOX
            ? 'quick-order.export.catalog.box.empty'
            : 'quick-order.export.catalog.empty';
        toast.error(translate(emptyMessageToTranslate));
        return;
      }

      setFetchedProducts(products);
      setIsExportLoading(true);
      setExportProgress(5);

      setTimeout(() => {
        setExportProgress(100);
        setIsExportLoading(false);
      }, 400);
    } catch (err) {
      toast.error(translate('quick-order.export.catalog.error'));
      setFetchedProducts([]);
      setIsExportLoading(false);
      setExportProgress(0);
    }
  };

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isExportLoading) {
      interval = setInterval(() => {
        setExportProgress((prev) => {
          if (prev < 90) return prev + 2;
          if (prev < 97) return prev + 0.2;
          return prev;
        });
      }, 150);
    }

    return () => clearInterval(interval);
  }, [isExportLoading]);

  useEffect(() => {
    if (!isExportLoading && exportProgress === 100 && fetchedProducts.length > 0) {
      setTimeout(() => {
        if (selectedStore?.key) {
          exportToCSV(fetchedProducts.map(mapCsvProduct), selectedStore.key, commercialOperationKey);
          toast.success(translate('quick-order.export.catalog.success'));
          setFetchedProducts([]);
          setExportProgress(0);
          setCommercialOperationKey(undefined);
        }
      }, 400);
    }
  }, [isExportLoading, exportProgress, fetchedProducts, selectedStore?.key]);

  useEffect(() => {
    if (!isUploadEnabled) handleClearAddToCartError();
  }, [isUploadEnabled]);

  useEffect(() => {
    if (files.length > 0) setIsAccordionOpen(false);
  }, [files.length]);

  return (
    <div className="flex flex-col gap-y-6">
      <div className="flex items-start justify-between">
        <div className="w-full">
          <Typography classStyle="h3">{translate('quick-order.add.csv')}</Typography>
          <div className="flex items-start justify-between">
            <Button
              variant="underlined"
              className="mt-4 font-bold"
              onClick={() =>
                handleDownloadCSV({ productTypeKey: ProductPackagingTypeList.SIMPLE, isCommercialOperation: false })
              }
              disabled={isExportLoading}
            >
              {translate('quick-order.export.catalog')}
            </Button>
            <Button
              variant="underlined"
              className="mt-4 font-bold"
              onClick={() =>
                handleDownloadCSV({ productTypeKey: ProductPackagingTypeList.BOX, isCommercialOperation: false })
              }
              disabled={isExportLoading}
            >
              {translate('quick-order.export.catalog.box')}
            </Button>
          </div>
          {commercialOperations.length > 0 && (
            <Accordion
              defaultIsExpanded={false}
              isExpanded={isAccordionOpen}
              onExpand={() => setIsAccordionOpen(true)}
              onCollapse={() => setIsAccordionOpen(false)}
              className="mt-4 border-0"
            >
              <Accordion.Button defaultSpacing={false} className="py-2 font-bold text-primary">
                {translate('quick-order.export.catalog.commercial-operation')} ({commercialOperations.length})
              </Accordion.Button>
              <Accordion.Panel defaultSpacing={false}>
                <div className="flex flex-col items-start gap-y-1 pt-1">
                  {commercialOperations.map((op) => (
                    <Button
                      key={op.categoryId}
                      variant="underlined"
                      className="font-bold"
                      onClick={() =>
                        handleDownloadCSV({
                          categoryId: op.categoryId,
                          categoryKey: op.key,
                          isCommercialOperation: true,
                        })
                      }
                      disabled={isExportLoading}
                    >
                      {op.name}
                    </Button>
                  ))}
                </div>
              </Accordion.Panel>
            </Accordion>
          )}
        </div>

        <IconButton onClick={onClose} icon={<CloseIcon className="h-6 w-6" />} />
      </div>
      {exportProgress > 0 && (
        <div className="relative mb-2 w-full overflow-hidden rounded-full border border-pink-900 bg-white-100 p-1 text-center text-sm">
          <div
            className="absolute left-0 top-0 h-full bg-pink-900 transition-all duration-300 ease-in-out"
            style={{ width: `${exportProgress}%` }}
          ></div>
          <span className="relative z-10 leading-[20px]">
            <Typography classStyle="h6" fontSize={14}>
              {translate('quick-order.add.cart.processing.file')}
            </Typography>
          </span>
        </div>
      )}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => inputFile?.current?.click()}
        className="flex h-[200px] w-full cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed border-secondary"
      >
        <input type="file" multiple onChange={onInputChange} ref={inputFile} className="hidden" />

        <IconButton
          variant={isUploadEnabled ? 'secondary' : 'primary'}
          icon={
            isUploadEnabled ? <CheckIcon className="h-6 w-6 text-white-100" /> : <Image src={FileIcon} alt="Upload" />
          }
          padding="p-4"
        />
        <div className="mt-4">
          <Typography align="center" classStyle="h6">
            {translate('quick-order.drag.csv')}
          </Typography>
          <Typography align="center" classStyle="b2" className="mt-1">
            {translate('quick-order.click.csv')}
          </Typography>
        </div>
      </div>

      {files.length > 0 && (
        <div className="flex flex-col gap-y-3">
          {files.map((file) => (
            <div className="relative" key={file.lastModified}>
              <div className={fileNameClassNames(file)}>
                <div className="flex justify-between px-2 py-3">
                  <Typography fontSize={14} className="truncate">
                    {file.name}
                  </Typography>
                  <Button variant="ghost" size="fit" onClick={() => onRemoveClick(file)}>
                    <CloseIcon className="w-4" />
                  </Button>
                </div>
                {!fileError[file.name] && loadingProgress[file.name] < 100 && (
                  <div className={loadingBarClassName(file)} />
                )}
              </div>
              {fileError[file.name] && (
                <Typography fontSize={14} className="mt-3 text-red-500">
                  {fileError[file.name]}
                </Typography>
              )}
            </div>
          ))}
        </div>
      )}
      {Object.entries(addToCartError).map(([key, msg]) => (
        <Typography key={key} fontSize={14} className="mt-3 text-red-500">
          {msg}
        </Typography>
      ))}

      {isUploadEnabled && (
        <div>
          {addToCartLoading ? (
            <div className="flex items-center gap-x-4">
              <Typography fontSize={14}>{translate('quick-order.add.cart.processing.file')}</Typography>
              <LoadingIcon svgWidth={20} svgHeight={20} className="fill-gray-700" />
            </div>
          ) : (
            <div className="flex gap-x-4">
              <Button variant="bordered" onClick={handleClearClick}>
                {translate('quick-order.click.clear')}
              </Button>
              <Button variant="primary" disabled={addToCartLoading} onClick={() => handleUploadClick(files)}>
                {translate('quick-order.click.upload')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
export default UploadPanel;
